/**
 * useLoadingStates Hook
 * Phase 1 Day 5 - Consolidated loading state management
 *
 * Manages all loading states across the application in a centralized hook.
 * This simplifies state management and provides a single source of truth
 * for loading indicators.
 *
 * @returns {Object} All loading states and management functions
 */

import { useUIStore } from "../stores";
import logger from "../utils/logger";

export const useLoadingStates = () => {
  // Get all loading states from UIStore
  const isRefreshing = useUIStore((state) => state.isRefreshing);
  const setRefreshing = useUIStore((state) => state.setRefreshing);

  const isSyncing = useUIStore((state) => state.isSyncing);
  const setSyncing = useUIStore((state) => state.setSyncing);

  const isSavingDisplayName = useUIStore((state) => state.isSavingDisplayName);
  const setSavingDisplayName = useUIStore(
    (state) => state.setSavingDisplayName
  );

  const isDownloadingSettings = useUIStore(
    (state) => state.isDownloadingSettings
  );
  const setDownloadingSettings = useUIStore(
    (state) => state.setDownloadingSettings
  );

  const loadingAction = useUIStore((state) => state.loadingAction);
  const setLoadingAction = useUIStore((state) => state.setLoadingAction);

  /**
   * Start refreshing
   */
  const startRefreshing = () => {
    logger.log("🔄 [LOADING STATE] Starting refresh", new Error().stack);
    setRefreshing(true);
  };

  /**
   * Stop refreshing
   */
  const stopRefreshing = () => {
    logger.log("✅ [LOADING STATE] Stopping refresh");
    setRefreshing(false);
  };

  /**
   * Start syncing
   */
  const startSyncing = () => {
    logger.log("🔄 [LOADING STATE] Starting sync", new Error().stack);
    setSyncing(true);
  };

  /**
   * Stop syncing
   */
  const stopSyncing = () => {
    logger.log("✅ [LOADING STATE] Stopping sync");
    setSyncing(false);
  };

  /**
   * Start saving display name
   */
  const startSavingDisplayName = () => {
    logger.log("[useLoadingStates] Starting display name save");
    setSavingDisplayName(true);
  };

  /**
   * Stop saving display name
   */
  const stopSavingDisplayName = () => {
    logger.log("[useLoadingStates] Stopping display name save");
    setSavingDisplayName(false);
  };

  /**
   * Start downloading settings
   */
  const startDownloadingSettings = () => {
    logger.log("[useLoadingStates] Starting settings download");
    setDownloadingSettings(true);
  };

  /**
   * Stop downloading settings
   */
  const stopDownloadingSettings = () => {
    logger.log("[useLoadingStates] Stopping settings download");
    setDownloadingSettings(false);
  };

  /**
   * Set loading action (e.g., 'lock', 'unlock')
   * @param {string|null} action - The action being loaded
   */
  const setLoading = (action) => {
    logger.log(`[useLoadingStates] Setting loading action:`, action);
    setLoadingAction(action);
  };

  /**
   * Clear loading action
   */
  const clearLoading = () => {
    logger.log("[useLoadingStates] Clearing loading action");
    setLoadingAction(null);
  };

  /**
   * Clear all loading states
   */
  const clearAllLoading = () => {
    logger.log("[useLoadingStates] Clearing all loading states");
    setRefreshing(false);
    setSyncing(false);
    setSavingDisplayName(false);
    setDownloadingSettings(false);
    setLoadingAction(null);
  };

  /**
   * Check if any loading state is active
   * @returns {boolean} True if any loading is active
   */
  const isAnyLoading = () => {
    return (
      isRefreshing ||
      isSyncing ||
      isSavingDisplayName ||
      isDownloadingSettings ||
      Boolean(loadingAction)
    );
  };

  return {
    // States
    isRefreshing,
    isSyncing,
    isSavingDisplayName,
    isDownloadingSettings,
    loadingAction,

    // Backward compatible setters
    setRefreshing,
    setSyncing,
    setSavingDisplayName,
    setDownloadingSettings,
    setLoadingAction,

    // Enhanced actions
    startRefreshing,
    stopRefreshing,
    startSyncing,
    stopSyncing,
    startSavingDisplayName,
    stopSavingDisplayName,
    startDownloadingSettings,
    stopDownloadingSettings,
    setLoading,
    clearLoading,

    // Utilities
    clearAllLoading,
    isAnyLoading,
  };
};
